import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Sidebar from '../components/sidebar/Sidebar';
import NavbarAdmin from '../components/navbar/NavbarAdmin';
import Modal from '../components/ui/Modal';
import { Eye } from 'lucide-react';
import {
  getAllReports,
  deleteReport,
  replyToReport,
} from '../../Admin/services/reportsService';
import swal from 'sweetalert';

const ReportsScreen = () => {
  const { slug } = useParams();
  const [reports, setReports] = useState([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortNewestFirst, setSortNewestFirst] = useState(true);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [modalText, setModalText] = useState('');
  const [modalTitle, setModalTitle] = useState('');

  const fetchReports = async () => {
    setIsLoading(true);
    try {
      const res = await getAllReports();
      setReports(res.data || []);
    } catch {
      console.error("Erreur de chargement");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleReply = (report) => {
    setSelectedReport(report);
    setReplyText(report.reply || '');
    setShowReplyModal(true);
  };

  const handleSubmitReply = async () => {
    if (!replyText.trim() || !selectedReport) return;
    setIsSending(true);
    try {
      await replyToReport(selectedReport._id, replyText.trim());
      setTimeout(() => {
        setIsSending(false);
        setShowReplyModal(false);
        fetchReports();
      }, 1000);
    } catch {
      setIsSending(false);
    }
  };

  const handleDelete = async (id) => {
    const confirm = await swal({
      title: 'Supprimer ?',
      text: 'Action irréversible.',
      icon: 'warning',
      buttons: ['Annuler', 'Supprimer'],
      dangerMode: true,
    });
    if (!confirm) return;
    try {
      await deleteReport(id);
      await swal('Supprimé', 'Rapport supprimé.', 'success');
      fetchReports();
    } catch {
      swal('Erreur', 'Échec de suppression.', 'error');
    }
  };

  const openTextModal = (text, title) => {
    setModalText(text);
    setModalTitle(title);
    setShowMessageModal(true);
  };

  const formatChunks = (text, size) => {
    return text.match(new RegExp(`.{1,${size}}`, 'g')) || [];
  };

  const filtered = reports
    .filter((r) =>
      r.name.toLowerCase().includes(search.toLowerCase()) ||
      r.email.toLowerCase().includes(search.toLowerCase()) ||
      r.message.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) =>
      sortNewestFirst
        ? new Date(b.createdAt) - new Date(a.createdAt)
        : new Date(a.createdAt) - new Date(b.createdAt)
    );

  const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(filtered.length / itemsPerPage);

  return (
    <div className="flex min-h-screen">
      <div className="md:hidden fixed top-0 left-0 right-0 z-50">
        <NavbarAdmin slug={slug} />
      </div>
      <div className="hidden md:flex">
        <Sidebar slug={slug} />
      </div>
      <main className="flex-1 md:ml-64 p-4 mt-16 md:mt-0">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-2">
            <h1 className="text-2xl font-bold text-paragaia">Rapports</h1>
            <div className="flex flex-wrap gap-2">
              <button onClick={fetchReports} className="bg-pink-200 text-white px-4 py-2 rounded-md hover:bg-pink-300">Rafraîchir</button>
              <button onClick={() => setSortNewestFirst(!sortNewestFirst)} className="bg-paragaia text-white px-4 py-2 rounded-md hover:bg-paragaiaLight">
                Trier: {sortNewestFirst ? "Nouveaux → Anciens" : "Anciens → Nouveaux"}
              </button>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="p-2 rounded bg-white border"
              >
                {[10, 25, 50, 100].map((num) => (
                  <option key={num} value={num}>Afficher {num}</option>
                ))}
              </select>
            </div>
          </div>

          <input
            type="text"
            placeholder="Rechercher par nom, email ou message..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mb-4 p-3 border rounded w-full"
          />

          <div className="hidden md:block">
            <table className="w-full bg-white rounded-lg shadow text-sm">
              <thead className="bg-paragaia text-white">
                <tr>
                  <th className="p-3">Nom</th>
                  <th className="p-3">Email</th>
                  <th className="p-3">Message</th>
                  <th className="p-3">Réponse</th>
                  <th className="p-3">Date</th>
                  <th className="p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((r) => (
                  <tr key={r._id} className="border-t hover:bg-gray-100">
                    <td className="p-3">{r.name}</td>
                    <td className="p-3">{r.email}</td>
                    <td className="p-3 max-w-xs">
                      {r.message.length > 40 ? `${r.message.slice(0, 40)}...` : r.message}
                      {r.message.length > 40 && (
                        <button onClick={() => openTextModal(r.message, 'Message complet')} className="ml-2 text-paragaia">
                          <Eye size={14} />
                        </button>
                      )}
                    </td>
                    <td className="p-3 max-w-xs">
                      {r.reply && r.reply.length > 10 ? `${r.reply.slice(0, 10)}...` : r.reply || '—'}
                      {r.reply && r.reply.length > 10 && (
                        <button onClick={() => openTextModal(r.reply, 'Réponse complète')} className="ml-2 text-paragaia">
                          <Eye size={14} />
                        </button>
                      )}
                    </td>
                    <td className="p-3">{new Date(r.createdAt).toLocaleString()}</td>
                    <td className="p-3 space-x-2">
                      <button onClick={() => handleReply(r)} disabled={!!r.reply} className={`px-3 py-1 rounded text-sm text-white ${r.reply ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}`}>
                        {r.reply ? 'Répondu' : 'Répondre'}
                      </button>
                      <button onClick={() => handleDelete(r._id)} className="px-3 py-1 bg-red-500 text-white rounded text-sm">Supprimer</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden space-y-4">
            {paginated.map((r) => (
              <div key={r._id} className="bg-white p-4 rounded-lg shadow">
                <p className="font-semibold text-gray-800 mb-1">{r.name}</p>
                <p className="text-sm text-gray-500 mb-1">{r.email}</p>
                <div className="text-sm text-gray-700 mb-2">
                  {r.message.length > 40 ? `${r.message.slice(0, 40)}...` : r.message}
                  {r.message.length > 40 && (
                    <button onClick={() => openTextModal(r.message, 'Message complet')} className="text-paragaia ml-1">
                      <Eye size={14} />
                    </button>
                  )}
                </div>
                {r.reply && (
                  <div className="text-sm text-green-700 bg-green-50 p-2 rounded mb-2 border border-green-200">
                    <strong>Réponse: </strong>
                    {r.reply.length > 10 ? (
                      <>
                        {r.reply.slice(0, 10)}...
                        <button onClick={() => openTextModal(r.reply, 'Réponse complète')} className="text-paragaia ml-1">
                          <Eye size={14} />
                        </button>
                      </>
                    ) : r.reply}
                  </div>
                )}
                <p className="text-xs text-gray-400 mb-3">{new Date(r.createdAt).toLocaleString()}</p>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => handleReply(r)} disabled={!!r.reply} className={`px-3 py-1 text-white rounded text-sm ${r.reply ? 'bg-gray-400' : 'bg-green-500 hover:bg-green-600'}`}>
                    {r.reply ? 'Répondu' : 'Répondre'}
                  </button>
                  <button onClick={() => handleDelete(r._id)} className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-sm">Supprimer</button>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center mt-6 space-x-1 items-center">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
                className={`px-3 py-1 rounded border ${
                  currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'bg-white hover:bg-gray-200'
                }`}
              >
                &lt;
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((page) => {
                  if (page === 1 || page === totalPages) return true;
                  if (page >= currentPage - 2 && page <= currentPage + 2) return true;
                  return false;
                })
                .map((page, i, arr) => {
                  const prev = arr[i - 1];
                  const isGap = prev && page - prev > 1;
                  return (
                    <span key={page} className="flex items-center">
                      {isGap && <span className="px-2 text-gray-500">...</span>}
                      <button
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 rounded border ${
                          currentPage === page
                            ? 'bg-paragaia text-white'
                            : 'bg-white text-paragaia hover:bg-paragaiaLight'
                        }`}
                      >
                        {page}
                      </button>
                    </span>
                  );
                })}

              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
                className={`px-3 py-1 rounded border ${
                  currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : 'bg-white hover:bg-gray-200'
                }`}
              >
                &gt;
              </button>
            </div>
          )}


          <Modal show={isLoading} onClose={() => {}}>
            <div className="flex justify-center py-10">
              <img src="/assets/loading.gif" alt="Chargement..." className="w-24 h-24" />
            </div>
          </Modal>
        </div>
      </main>

      <Modal show={showReplyModal} onClose={() => setShowReplyModal(false)}>
        {isSending ? (
          <div className="flex justify-center py-10">
            <img src="/assets/loading.gif" alt="Chargement..." className="w-24 h-24" />
          </div>
        ) : (
          <div className="space-y-4">
            <h2 className="text-lg font-bold">Répondre à {selectedReport?.name}</h2>
            <textarea className="w-full p-2 border rounded" rows={5} value={replyText} onChange={(e) => setReplyText(e.target.value)} />
            <button onClick={handleSubmitReply} className="bg-paragaia text-white px-4 py-2 rounded">Envoyer</button>
          </div>
        )}
      </Modal>

      <Modal show={showMessageModal} onClose={() => setShowMessageModal(false)}>
        <div className="max-w-lg p-4 space-y-4">
          <h2 className="text-lg font-bold text-gray-800">{modalTitle}</h2>
          <textarea className="w-full border p-3 rounded bg-gray-50 text-sm text-gray-700" rows={Math.min(20, Math.ceil(modalText.length / 40) + 2)} readOnly value={formatChunks(modalText, 40).join('\n')} />
          <button onClick={() => setShowMessageModal(false)} className="bg-paragaia text-white px-4 py-2 rounded">Fermer</button>
        </div>
      </Modal>
    </div>
  );
};

export default ReportsScreen;